import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateCapsule } from '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateIcoSphere } from '@babylonjs/core/Meshes/Builders/icoSphereBuilder';
import { CreateLines } from '@babylonjs/core/Meshes/Builders/linesBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';
import {
  DIFFICULTY_MULTIPLIER,
  KICK_ANGLE,
  KICK_RANGE,
  MONSTER_ATTACK_ANGLE,
  MONSTER_ATTACK_CONNECT_TIME,
  MONSTER_ATTACK_RANGE,
  MONSTER_ATTACK_WINDUP,
  MONSTER_DETECTION_RANGE,
  MONSTER_ROTATION_SPEED,
  MONSTER_SEPARATION_RADIUS,
  MONSTER_SEPARATION_STRENGTH,
  MONSTER_SPEED,
  MONSTER_PURSUIT_SPREAD,
} from '../config/GameConfig';
import type { MonsterSpawn, MonsterType } from '../levels/LevelTypes';
import type { GridNavigator } from '../navigation/GridNavigator';
import { AttackCycle, canKick, mouthConnects } from '../systems/DirectionalCombat';
import { distance2D, rotateToward } from '../utils/DirectionalMath';

export type MonsterState = 'IDLE' | 'PATROL' | 'INVESTIGATE' | 'SEARCH' | 'CHASE' | 'ATTACK' | 'STUNNED' | 'DEFEATED';

interface MonsterProfile {
  speed: number;
  rotation: number;
  detection: number;
  attackRange: number;
  attackAngle: number;
  scale: number;
  color: string;
  accent: string;
  prediction: number;
}

const PROFILES: Record<MonsterType, MonsterProfile> = {
  stalker: { speed: 1, rotation: 1, detection: 1, attackRange: 1, attackAngle: 1, scale: 1, color: '#44A89B', accent: '#F0C35C', prediction: 0 },
  chaser: { speed: 1.38, rotation: 1.2, detection: 1.18, attackRange: 1, attackAngle: 0.95, scale: 1, color: '#E56F55', accent: '#FFD66B', prediction: 0.55 },
  ambusher: { speed: 1.12, rotation: 1.1, detection: 0.72, attackRange: 1, attackAngle: 1, scale: 0.95, color: '#8259B8', accent: '#F5A7D6', prediction: 0.2 },
  patrol: { speed: 0.9, rotation: 0.9, detection: 1.05, attackRange: 1, attackAngle: 1, scale: 1, color: '#D18A36', accent: '#9BDB70', prediction: 0 },
  heavy: { speed: 0.72, rotation: 0.62, detection: 1.05, attackRange: 1.16, attackAngle: 1.18, scale: 1.34, color: '#527FC2', accent: '#F1B866', prediction: 0 },
  small: { speed: 1.48, rotation: 1.52, detection: 0.9, attackRange: 0.78, attackAngle: 0.8, scale: 0.72, color: '#E68AB6', accent: '#7BD2C4', prediction: 0.3 },
  curious: { speed: 1.02, rotation: 0.95, detection: 1.28, attackRange: 1, attackAngle: 0.94, scale: 0.92, color: '#74A94B', accent: '#E9D96A', prediction: 0.1 },
};

export interface MonsterContext {
  playerPosition: Vector3;
  playerVelocity: Vector3;
  playerSprinting: boolean;
  floorHeight: (x: number, z: number) => number;
  paused: boolean;
  nearbyMonsters: readonly BaseMonster[];
  onDetected: (monster: BaseMonster) => void;
  onAttackStart: (monster: BaseMonster) => void;
  onCaught: (monster: BaseMonster) => void;
  onCloseEscape: (monster: BaseMonster) => void;
}

export abstract class BaseMonster {
  readonly collider: Mesh;
  readonly visual: TransformNode;
  state: MonsterState;
  defeated = false;
  readonly type: MonsterType;
  readonly index: number;
  protected readonly profile: MonsterProfile;
  private readonly body: Mesh;
  private readonly mouth: Mesh;
  private readonly upperLip: Mesh;
  private readonly lowerLip: Mesh;
  private readonly eyes: Mesh[] = [];
  private readonly feet: Mesh[] = [];
  private readonly kickRing: Mesh;
  private readonly mouthSector: Mesh;
  private readonly kickSector: Mesh;
  private readonly detectionRing: Mesh;
  private readonly forwardLine: LinesMesh;
  private attackCycle?: AttackCycle;
  private path: { x: number; z: number }[] = [];
  private pathIndex = 0;
  private pathCooldown = 0;
  private pathDebug?: LinesMesh;
  private lastKnown?: Vector3;
  private lostSightTimer = 0;
  private stateTimer = 0;
  private defeatTimer = 0;
  private animationTime = 0;
  private readonly movementPhase: number;
  private readonly movementTempo: number;
  private readonly weaveAmount: number;
  private patrolIndex = 0;
  private debug = false;
  private alerted = false;
  private mouthMaterial: StandardMaterial;
  private bodyMaterial: StandardMaterial;

  constructor(
    protected readonly scene: Scene,
    spawn: MonsterSpawn,
    index: number,
    protected readonly navigator: GridNavigator,
    levelId: number,
  ) {
    this.type = spawn.type;
    this.index = index;
    this.movementPhase = Math.random() * Math.PI * 2;
    this.movementTempo = 0.72 + Math.random() * 0.42;
    this.weaveAmount = 0.075 + Math.random() * 0.085;
    this.profile = PROFILES[spawn.type];
    this.state = spawn.type === 'patrol' ? 'PATROL' : 'IDLE';
    this.spawn = spawn;
    const difficulty = 1 + Math.max(0, levelId - 1) * DIFFICULTY_MULTIPLIER;
    this.speed = MONSTER_SPEED * this.profile.speed * difficulty;
    this.turnSpeed = MONSTER_ROTATION_SPEED * this.profile.rotation * Math.sqrt(difficulty);
    this.detectionRange = MONSTER_DETECTION_RANGE * this.profile.detection * difficulty;
    this.attackRange = MONSTER_ATTACK_RANGE * this.profile.attackRange;
    this.attackAngle = MONSTER_ATTACK_ANGLE * this.profile.attackAngle;

    const s = this.profile.scale;
    this.collider = CreateCapsule(`monster-${index}-collider`, { height: 2.05 * s, radius: 0.7 * s, tessellation: 10 }, scene);
    this.collider.position.set(spawn.x, 1.03 * s, spawn.z);
    this.collider.rotation.y = spawn.yaw;
    this.collider.ellipsoid = new Vector3(0.64 * s, 0.95 * s, 0.64 * s);
    this.collider.checkCollisions = true;
    this.collider.isVisible = false;
    this.collider.isPickable = false;
    this.collider.metadata = { isMonster: true };

    this.visual = new TransformNode(`monster-${index}-${spawn.type}`, scene);
    this.visual.parent = this.collider;
    this.visual.position.y = -1.02 * s;

    this.bodyMaterial = this.makeMaterial(`monster-${index}-skin`, this.profile.color);
    const accent = this.makeMaterial(`monster-${index}-accent`, this.profile.accent, '#2A2108');
    this.mouthMaterial = this.makeMaterial(`monster-${index}-mouth`, '#3C1F38');
    const tooth = this.makeMaterial(`monster-${index}-teeth`, '#FFF0C6', '#30230D');
    const pupil = this.makeMaterial(`monster-${index}-pupil`, '#172F36');

    this.body = CreateIcoSphere(`monster-${index}-body`, { radius: 1.04 * s, subdivisions: 2, flat: false }, scene);
    this.body.scaling.set(1.05, spawn.type === 'heavy' ? 1.14 : 1, spawn.type === 'small' ? 0.92 : 1);
    this.body.position.y = 1.18 * s;
    this.body.material = this.bodyMaterial;
    this.body.parent = this.visual;

    this.mouth = CreateSphere(`monster-${index}-mouth`, { diameter: 1.34 * s, segments: 16 }, scene);
    this.mouth.scaling.set(0.86, 0.7, 0.13);
    this.mouth.position.set(0, 1.11 * s, 0.95 * s);
    this.mouth.material = this.mouthMaterial;
    this.mouth.parent = this.visual;
    this.upperLip = CreateTorus(`monster-${index}-upper-lip`, { diameter: 1.13 * s, thickness: 0.14 * s, tessellation: 16 }, scene);
    this.upperLip.scaling.y = 0.68;
    this.upperLip.rotation.x = Math.PI / 2;
    this.upperLip.position.set(0, 1.13 * s, 1.09 * s);
    this.upperLip.material = accent;
    this.upperLip.parent = this.visual;
    this.lowerLip = this.upperLip.clone(`monster-${index}-lower-lip`, this.visual)!;

    for (let toothIndex = 0; toothIndex < (spawn.type === 'small' ? 3 : 4); toothIndex += 1) {
      const x = (toothIndex - (spawn.type === 'small' ? 1 : 1.5)) * 0.25 * s;
      const top = CreateCylinder(`monster-${index}-top-tooth-${toothIndex}`, { height: 0.28 * s, diameterTop: 0, diameterBottom: 0.18 * s, tessellation: 5 }, scene);
      top.position.set(x, 1.47 * s, 1.12 * s); top.material = tooth; top.parent = this.visual;
      const bottom = CreateCylinder(`monster-${index}-bottom-tooth-${toothIndex}`, { height: 0.24 * s, diameterTop: 0.18 * s, diameterBottom: 0, tessellation: 5 }, scene);
      bottom.position.set(x, 0.78 * s, 1.12 * s); bottom.material = tooth; bottom.parent = this.visual;
    }

    for (const x of [-0.44, 0.44]) {
      const eye = CreateSphere(`monster-${index}-eye`, { diameter: 0.45 * s, segments: 10 }, scene);
      eye.position.set(x * s, 1.93 * s, 0.62 * s); eye.material = tooth; eye.parent = this.visual;
      const dot = CreateSphere(`monster-${index}-pupil`, { diameter: 0.19 * s, segments: 8 }, scene);
      dot.position.set(x * s, 1.94 * s, 0.82 * s); dot.material = pupil; dot.parent = this.visual;
      this.eyes.push(dot);
    }
    for (const x of [-0.52, 0.52]) {
      const foot = CreateSphere(`monster-${index}-foot`, { diameter: 0.48 * s, segments: 8 }, scene);
      foot.scaling.set(1.2, 0.58, 1.5); foot.position.set(x * s, 0.27 * s, 0.12 * s); foot.material = accent; foot.parent = this.visual;
      this.feet.push(foot);
    }
    this.addSilhouetteDetails(accent, s);

    this.kickRing = CreateTorus(`monster-${index}-kick-highlight`, { diameter: 2.35 * s, thickness: 0.105, tessellation: 32 }, scene);
    this.kickRing.rotation.x = Math.PI / 2; this.kickRing.position.y = 0.06; this.kickRing.material = this.makeMaterial(`monster-${index}-kick-glow`, '#87F4AE', '#2CA66E');
    this.kickRing.parent = this.collider; this.kickRing.isVisible = false;
    this.mouthSector = this.createSector(`monster-${index}-mouth-cone`, this.attackRange, this.attackAngle, 0);
    this.mouthSector.material = this.transparentMaterial(`monster-${index}-danger-zone`, '#FF5D5D', 0.34); this.mouthSector.parent = this.collider;
    this.kickSector = this.createSector(`monster-${index}-kick-zone`, KICK_RANGE, KICK_ANGLE, Math.PI);
    this.kickSector.material = this.transparentMaterial(`monster-${index}-safe-zone`, '#58E69B', 0.2); this.kickSector.parent = this.collider;
    this.detectionRing = CreateTorus(`monster-${index}-detection`, { diameter: this.detectionRange * 2, thickness: 0.045, tessellation: 48 }, scene);
    this.detectionRing.rotation.x = Math.PI / 2; this.detectionRing.position.y = 0.045; this.detectionRing.material = this.transparentMaterial(`monster-${index}-detect-mat`, '#F9D65C', 0.5); this.detectionRing.parent = this.collider;
    this.forwardLine = CreateLines(`monster-${index}-forward`, { points: [new Vector3(0, 0.12, 0), new Vector3(0, 0.12, 3.2 * s)] }, scene);
    this.forwardLine.color = Color3.FromHexString('#FFEA66'); this.forwardLine.parent = this.collider;
    this.setDebugVisible(false);
  }

  readonly spawn: MonsterSpawn;
  readonly speed: number;
  readonly turnSpeed: number;
  readonly detectionRange: number;
  readonly attackRange: number;
  readonly attackAngle: number;

  get position(): Vector3 { return this.collider.position; }
  get yaw(): number { return this.collider.rotation.y; }

  update(deltaSeconds: number, context: MonsterContext): void {
    if (context.paused) return;
    this.animationTime += deltaSeconds;
    if (this.defeated) { this.updateDefeat(deltaSeconds); return; }

    const dist = distance2D(this.position, context.playerPosition);
    const visible = dist <= this.detectionRange && this.hasLineOfSight(context.playerPosition);
    const kickable = canKick({ position: this.position, yaw: this.yaw, defeated: false }, context.playerPosition);
    this.kickRing.isVisible = kickable;
    const frontalWarning = dist < Math.max(5, this.attackRange * 1.8)
      && this.angleTo(context.playerPosition) < this.attackAngle * 0.62;
    this.mouthMaterial.emissiveColor = frontalWarning ? Color3.FromHexString('#7A1834') : Color3.Black();
    this.eyes.forEach((eye) => { eye.position.z = (frontalWarning ? 0.87 : 0.82) * this.profile.scale; });

    if (this.state === 'ATTACK') {
      this.updateAttack(deltaSeconds, context);
      this.animate(false, true);
      return;
    }

    if (visible && (this.type !== 'ambusher' || dist < this.detectionRange * 0.72)) {
      this.lastKnown = context.playerPosition.clone();
      this.lostSightTimer = 0;
      if (this.state !== 'CHASE') {
        this.state = 'CHASE'; this.stateTimer = 0; this.requestPath(context.playerPosition);
        if (!this.alerted) { this.alerted = true; context.onDetected(this); }
      }
    } else if (this.type === 'curious' && context.playerSprinting && dist < this.detectionRange * 1.45 && this.state !== 'CHASE') {
      this.lastKnown = context.playerPosition.clone();
      this.state = 'INVESTIGATE'; this.stateTimer = 4.5; this.requestPath(context.playerPosition);
    } else if (this.state === 'CHASE') {
      this.lostSightTimer += deltaSeconds;
      if (this.lostSightTimer > 2.1) { this.state = 'SEARCH'; this.stateTimer = 4.2; if (this.lastKnown) this.requestPath(this.lastKnown); }
    }

    if (this.state === 'CHASE' && mouthConnects({
      position: this.position, yaw: this.yaw, defeated: false, attackRange: this.attackRange, attackAngle: this.attackAngle,
    }, context.playerPosition)) {
      this.state = 'ATTACK';
      this.attackCycle = new AttackCycle(MONSTER_ATTACK_CONNECT_TIME, MONSTER_ATTACK_WINDUP);
      context.onAttackStart(this);
      this.animate(false, true);
      return;
    }

    let moving = false;
    this.pathCooldown -= deltaSeconds;
    if (this.state === 'CHASE') {
      const predicted = context.playerPosition.add(context.playerVelocity.scale(this.profile.prediction));
      if (this.pathCooldown <= 0) this.requestPath(predicted);
      moving = this.followPath(deltaSeconds, context.floorHeight, context.nearbyMonsters);
    } else if (this.state === 'INVESTIGATE' || this.state === 'SEARCH') {
      this.stateTimer -= deltaSeconds;
      moving = this.followPath(deltaSeconds, context.floorHeight, context.nearbyMonsters);
      if (this.stateTimer <= 0 || (!moving && this.pathIndex >= this.path.length)) {
        this.state = this.spawn.patrol?.length ? 'PATROL' : 'IDLE'; this.alerted = false;
      }
    } else if (this.state === 'PATROL' && this.spawn.patrol?.length) {
      const destination = this.spawn.patrol[this.patrolIndex]!;
      if (!this.path.length || this.pathIndex >= this.path.length) this.requestPath(new Vector3(destination.x, 0, destination.z));
      moving = this.followPath(deltaSeconds * 0.62, context.floorHeight, context.nearbyMonsters);
      if (distance2D(this.position, destination) < 1) { this.patrolIndex = (this.patrolIndex + 1) % this.spawn.patrol.length; this.path = []; }
    } else {
      this.collider.rotation.y += Math.sin(this.animationTime * 0.7 + this.index) * deltaSeconds * 0.08;
    }
    this.animate(moving, false);
  }

  isKickableFrom(playerPosition: Vector3): boolean {
    return canKick({ position: this.position, yaw: this.yaw, defeated: this.defeated }, playerPosition);
  }

  defeat(playerPosition: Vector3): void {
    if (this.defeated) return;
    this.defeated = true; this.state = 'DEFEATED'; this.defeatTimer = 0;
    this.collider.checkCollisions = false;
    this.kickRing.isVisible = false;
    const away = this.position.subtract(playerPosition); away.y = 0;
    if (away.lengthSquared() < 0.1) away.set(0, 0, 1);
    away.normalize(); this.defeatDirection = away;
  }

  setDebugVisible(visible: boolean): void {
    this.debug = visible;
    this.collider.isVisible = visible;
    this.mouthSector.isVisible = visible;
    this.kickSector.isVisible = visible;
    this.detectionRing.isVisible = visible;
    this.forwardLine.isVisible = visible;
    if (this.pathDebug) this.pathDebug.isVisible = visible;
  }

  dispose(): void {
    this.pathDebug?.dispose();
    this.collider.dispose(false, true);
  }

  protected addSilhouetteDetails(accent: StandardMaterial, scale: number): void {
    const count = this.type === 'heavy' ? 5 : this.type === 'small' ? 2 : 3;
    for (let i = 0; i < count; i += 1) {
      const horn = CreateCylinder(`monster-${this.index}-tuft-${i}`, { height: 0.55 * scale, diameterTop: 0, diameterBottom: 0.26 * scale, tessellation: 6 }, this.scene);
      horn.position.set((i - (count - 1) / 2) * 0.32 * scale, (2.13 + (i % 2) * 0.15) * scale, 0);
      horn.rotation.z = (i - (count - 1) / 2) * -0.22; horn.material = accent; horn.parent = this.visual;
    }
  }

  private defeatDirection = Vector3.Zero();

  private updateDefeat(deltaSeconds: number): void {
    this.defeatTimer += deltaSeconds;
    const s = this.profile.scale;
    if (this.defeatTimer < 0.9) {
      this.collider.position.addInPlace(this.defeatDirection.scale(deltaSeconds * (3.4 - this.defeatTimer * 2)));
      this.collider.position.y = 1.03 * s + Math.abs(Math.sin(this.defeatTimer * Math.PI * 3)) * (0.8 - this.defeatTimer * 0.4);
      this.visual.rotation.z += deltaSeconds * 8;
      this.visual.rotation.y += deltaSeconds * 5;
      this.body.scaling.y = 1 + Math.sin(this.defeatTimer * 14) * 0.14;
    } else {
      const shrink = Math.max(0, 1 - (this.defeatTimer - 0.9) * 3.2);
      this.visual.scaling.setAll(shrink);
      if (shrink <= 0.01) this.collider.setEnabled(false);
    }
  }

  private updateAttack(deltaSeconds: number, context: MonsterContext): void {
    if (!this.attackCycle) return;
    const result = this.attackCycle.update(deltaSeconds,
      {
        position: this.position, yaw: this.yaw, defeated: this.defeated,
        attackRange: this.attackRange, attackAngle: this.attackAngle,
      }, context.playerPosition);
    if (result === 'caught') context.onCaught(this);
    if (result === 'missed') context.onCloseEscape(this);
    if (this.attackCycle.finished) {
      this.attackCycle = undefined;
      this.state = result === 'caught' ? 'IDLE' : 'SEARCH';
      this.stateTimer = 2.8;
      this.requestPath(context.playerPosition);
    }
  }

  private animate(moving: boolean, attacking: boolean): void {
    const s = this.profile.scale;
    const walk = moving ? Math.sin(this.animationTime * this.speed * 5) : 0;
    this.visual.position.y = -1.02 * s + (moving ? Math.abs(walk) * 0.11 : Math.sin(this.animationTime * 2 + this.index) * 0.035);
    this.visual.rotation.z = moving ? Math.sin(this.animationTime * this.speed * 2.5) * 0.08 : 0;
    this.feet[0]!.position.z = (0.12 + walk * 0.34) * s;
    this.feet[1]!.position.z = (0.12 - walk * 0.34) * s;
    const snap = attacking ? Math.abs(Math.sin(this.animationTime * 15)) : 0;
    this.mouth.scaling.y = 0.7 + snap * 0.25;
    this.upperLip.position.y = (1.13 + snap * 0.14) * s;
    this.lowerLip.position.y = (1.13 - snap * 0.14) * s;
  }

  private followPath(deltaSeconds: number, floorHeight: MonsterContext['floorHeight'], nearbyMonsters: readonly BaseMonster[]): boolean {
    const target = this.path[this.pathIndex];
    if (!target) return false;
    const direction = new Vector3(target.x - this.position.x, 0, target.z - this.position.z);
    if (direction.lengthSquared() < 0.6) { this.pathIndex += 1; return this.pathIndex < this.path.length; }
    direction.normalize();
    // Give every monster a mild personal weave and pace rhythm without leaving its valid navigation path.
    const sideways = new Vector3(direction.z, 0, -direction.x);
    const weave = Math.sin(this.animationTime * this.movementTempo + this.movementPhase) * this.weaveAmount;
    direction.addInPlace(sideways.scale(weave)).normalize();
    // Steer gently away from neighbors so a group fans into separate lanes instead of forming one ball.
    const separation = Vector3.Zero();
    for (const neighbor of nearbyMonsters) {
      if (neighbor.index === this.index || neighbor.defeated) continue;
      const away = this.position.subtract(neighbor.position); away.y = 0;
      const distance = away.length();
      if (distance < 0.01 || distance >= MONSTER_SEPARATION_RADIUS) continue;
      separation.addInPlace(away.scale((MONSTER_SEPARATION_RADIUS - distance) / (MONSTER_SEPARATION_RADIUS * distance)));
    }
    if (separation.lengthSquared() > 0.0001) {
      separation.normalize().scaleInPlace(MONSTER_SEPARATION_STRENGTH);
      direction.addInPlace(separation).normalize();
    }
    const desiredYaw = Math.atan2(direction.x, direction.z);
    this.collider.rotation.y = rotateToward(this.collider.rotation.y, desiredYaw, this.turnSpeed * deltaSeconds);
    const forward = new Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw));
    const alignment = Math.max(0.18, Vector3.Dot(forward, direction));
    const paceVariation = 0.955 + Math.sin(this.animationTime * 0.61 + this.movementPhase * 1.7) * 0.045;
    this.collider.moveWithCollisions(forward.scale(this.speed * paceVariation * alignment * deltaSeconds));
    const desiredY = floorHeight(this.position.x, this.position.z) + 1.03 * this.profile.scale;
    this.position.y += (desiredY - this.position.y) * Math.min(1, deltaSeconds * 8);
    return true;
  }

  private requestPath(target: Vector3): void {
    const spreadTarget = this.state === 'PATROL' ? target : this.personalPursuitTarget(target);
    this.path = this.navigator.findPath(this.position, spreadTarget);
    this.pathIndex = Math.min(1, Math.max(0, this.path.length - 1));
    this.pathCooldown = 0.55 + (this.index % 4) * 0.08;
    this.updateDebugPath();
  }

  private personalPursuitTarget(target: Vector3): Vector3 {
    const radius = MONSTER_PURSUIT_SPREAD * (0.78 + (this.index % 4) * 0.24);
    const angle = this.movementPhase + Math.sin(this.animationTime * 0.22 + this.index) * 0.35;
    return target.add(new Vector3(Math.cos(angle) * radius, 0, Math.sin(angle) * radius));
  }

  private updateDebugPath(): void {
    this.pathDebug?.dispose();
    if (this.path.length < 2) return;
    this.pathDebug = CreateLines(`monster-${this.index}-nav-path`, {
      points: this.path.map((point) => new Vector3(point.x, 0.16, point.z)),
    }, this.scene);
    this.pathDebug.color = Color3.FromHexString('#5CDDF2');
    this.pathDebug.isVisible = this.debug;
  }

  private hasLineOfSight(target: Vector3): boolean {
    const origin = this.position.add(new Vector3(0, 0.4, 0));
    const direction = target.add(new Vector3(0, 0.2, 0)).subtract(origin);
    const length = direction.length();
    if (length < 0.1) return true;
    const hit = this.scene.pickWithRay(new Ray(origin, direction.normalize(), length), (mesh) => Boolean(mesh.metadata?.isObstacle));
    return !hit?.hit;
  }

  private angleTo(target: Vector3): number {
    const forward = new Vector3(Math.sin(this.yaw), 0, Math.cos(this.yaw));
    const toTarget = target.subtract(this.position); toTarget.y = 0; toTarget.normalize();
    return Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(forward, toTarget)))) * 180 / Math.PI;
  }

  private createSector(name: string, radius: number, fullAngle: number, centerAngle: number): Mesh {
    const segments = 28;
    const positions: number[] = [0, 0.075, 0];
    const indices: number[] = [];
    const half = fullAngle * Math.PI / 360;
    for (let i = 0; i <= segments; i += 1) {
      const angle = centerAngle - half + i / segments * half * 2;
      positions.push(Math.sin(angle) * radius, 0.075, Math.cos(angle) * radius);
      if (i > 0) indices.push(0, i, i + 1);
    }
    const mesh = new Mesh(name, this.scene);
    const data = new VertexData(); data.positions = positions; data.indices = indices;
    data.normals = Array.from({ length: positions.length }, (_, i) => i % 3 === 1 ? 1 : 0); data.applyToMesh(mesh);
    mesh.isPickable = false;
    return mesh;
  }

  private makeMaterial(name: string, diffuse: string, emissive = '#000000'): StandardMaterial {
    const mat = new StandardMaterial(name, this.scene);
    mat.diffuseColor = Color3.FromHexString(diffuse); mat.emissiveColor = Color3.FromHexString(emissive);
    mat.specularColor = new Color3(0.08, 0.08, 0.08);
    return mat;
  }

  private transparentMaterial(name: string, color: string, alpha: number): StandardMaterial {
    const mat = this.makeMaterial(name, color, color); mat.alpha = alpha; mat.disableDepthWrite = true; mat.backFaceCulling = false; return mat;
  }
}

class ConfiguredMonster extends BaseMonster {}

export function createMonster(scene: Scene, spawn: MonsterSpawn, index: number, navigator: GridNavigator, levelId: number): BaseMonster {
  return new ConfiguredMonster(scene, spawn, index, navigator, levelId);
}
